/**
 * CircleInviteByToken — PPL-215 (web: public circle-invite landing).
 *
 * Public `/c/:token` landing. NO auth required — `resolveCircleInviteByToken`
 * and `declineCircleInviteByToken` are both UNAUTHENTICATED callables (token
 * possession is the capability). Only accepting needs an account.
 *
 * Routing invariant (mirrors `/i/:token`): this is a BARE route in App.jsx —
 * outside ProtectedRoute and outside APP_SHELL_PATHS.
 *
 * Behaviour by auth state
 * ────────────────────────
 * Logged-OUT:
 *   accept  → /register?continueUrl=/c/:token?action=accept  (action folded
 *             INTO continueUrl so it survives register → verify → login, the
 *             PPL-159 fix)
 *   decline → declineCircleInviteByToken(token) → home; no signup needed
 *
 * Logged-IN:
 *   accept  → acceptCircleInviteByToken(token) → /circles/:slug
 *   decline → declineCircleInviteByToken(token) → /circles
 *
 * Post-signup bind: Register.jsx navigates to the continueUrl once the user
 * verifies, so this component detects `user + ?action=accept` and completes the
 * token bind automatically.
 *
 * Wrong account (PPL-180 handoff): sign out and route to
 * `/login?redirect=/c/:token?action=accept`. A bare reload of the token URL
 * strands the user on the signed-out landing — do not "fix" it back to that.
 *
 * Identity register: circles are the PERSONAL half. The resolver returns the
 * inviter's firstName (never their handle or email) and that is all we render.
 *
 * Privacy invariant: never render anything beyond the CircleInvitePublicView
 * shape — circle name/description/memberCount/imageUrl, inviter firstName +
 * avatar, and the lifecycle flags.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Users } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import {
  acceptCircleInviteByToken,
  declineCircleInviteByToken,
  resolveCircleInviteByToken,
} from '../services/circleService';
import { slugify } from '../utils/circleSlug';
import type { CircleInvitePublicView } from '../types/circles';
import { capture, hashInviteToken } from '../services/posthog';

type ResponseId = 'accept' | 'decline';

const SHELL: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'var(--bg-primary, #0e0712)',
};

function TerminalState({
  heading,
  body,
  linkLabel = 'go to pplkpr →',
  linkTo = '/',
}: {
  heading: string;
  body: string;
  linkLabel?: string;
  linkTo?: string;
}) {
  return (
    <div style={SHELL}>
      <div style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
          {heading}
        </h1>
        <p style={{ margin: 0, color: 'var(--text-muted, rgba(220,200,240,0.7))', lineHeight: 1.55 }}>
          {body}
        </p>
        <div style={{ marginTop: 30 }}>
          <Link to={linkTo} style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
            {linkLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function CircleInviteByToken() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const actionParam = searchParams.get('action') as ResponseId | null;

  const [view, setView] = useState<CircleInvitePublicView | null>(null);
  const [resolveLoading, setResolveLoading] = useState(true);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const [responding, setResponding] = useState<ResponseId | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [autoAcceptFired, setAutoAcceptFired] = useState(false);

  // ── resolve on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setResolveError('missing invite token.');
      setResolveLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await resolveCircleInviteByToken(token);
        if (cancelled) return;
        setView(data);
        // Referral-funnel anchor — fires for anonymous visitors too, since the
        // anon distinct_id is what an alias-at-signup later joins on. The raw
        // token never leaves the browser, only its truncated hash.
        const inviteTokenHash = await hashInviteToken(token);
        capture('circle_invite_opened', {
          invite_token_hash: inviteTokenHash,
          circle_id: data.circleId ?? null,
          expired: !!data.expired,
          already_accepted: !!data.alreadyAccepted,
          viewer_logged_in: !!user,
          viewer_role: data.viewerRole ?? 'anonymous',
        });
      } catch (err) {
        if (!cancelled) {
          setResolveError(err instanceof Error ? err.message : 'could not load this invitation.');
        }
      } finally {
        if (!cancelled) setResolveLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doAccept = async (tok: string) => {
    setResponding('accept');
    setResponseError(null);
    try {
      const res = await acceptCircleInviteByToken(tok);
      const slug = slugify(res.circleName || view?.circle?.name || '');
      navigate(slug ? `/circles/${slug}` : '/circles', {
        replace: true,
        state: { circleId: res.circleId },
      });
    } catch (err) {
      setResponseError(err instanceof Error ? err.message : 'could not accept the invitation.');
      setResponding(null);
    }
  };

  // Auto-accept after a signup/login round trip returns with ?action=accept.
  //
  // Waits for the resolver rather than firing blind on mount: an expired,
  // already-answered, own or wrong-account invitation renders a terminal state
  // that has nowhere to show `responseError`, so an accept fired underneath it
  // would fail invisibly. Gating on the resolved view means the terminal screen
  // IS the explanation, and the callable is never called when it can only 403.
  useEffect(() => {
    if (!user || !token || actionParam !== 'accept' || autoAcceptFired) return;
    if (resolveLoading || resolveError || !view) return;
    if (view.expired || view.alreadyAccepted) return;
    if (view.viewerRole === 'inviter' || view.viewerRole === 'stranger') return;
    setAutoAcceptFired(true);
    // Strip the query param so a back-nav can't re-trigger it.
    window.history.replaceState({}, '', window.location.pathname);
    void doAccept(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, actionParam, autoAcceptFired, resolveLoading, resolveError, view]);

  const handleResponse = async (responseId: ResponseId) => {
    if (responding || !token) return;
    setResponseError(null);

    void (async () => {
      const inviteTokenHash = await hashInviteToken(token);
      capture('circle_invite_landing_response', {
        response: responseId,
        logged_in: !!user,
        invite_token_hash: inviteTokenHash,
        circle_id: view?.circleId ?? null,
      });
    })();

    if (responseId === 'accept') {
      if (user) {
        await doAccept(token);
      } else {
        navigate(`/register?continueUrl=${encodeURIComponent(`/c/${token}?action=accept`)}`);
      }
      return;
    }

    // Decline works logged-out: the callable is unauthenticated, and a
    // logged-in-but-unbound invitee can't use the inviteId path anyway (the
    // ownership guard rejects a placeholder's empty inviteeUid).
    setResponding('decline');
    try {
      await declineCircleInviteByToken(token);
    } catch (err) {
      setResponseError(
        err instanceof Error ? err.message : 'could not record your decline — please try again',
      );
      setResponding(null);
      return;
    }
    setResponding(null);
    navigate(user ? '/circles' : '/');
  };

  // ── loading ───────────────────────────────────────────────────────────────
  if (resolveLoading) {
    return (
      <div style={SHELL}>
        <span style={{ color: 'rgba(220,200,240,0.6)', fontSize: 14 }}>loading your invitation…</span>
      </div>
    );
  }

  if (resolveError) {
    return <TerminalState heading="invitation not available" body={resolveError} />;
  }

  if (view?.expired) {
    return (
      <TerminalState
        heading="this invitation has expired"
        body="ask the person who invited you to send a new one."
      />
    );
  }

  if (view?.alreadyAccepted) {
    return (
      <TerminalState
        heading="already answered"
        body={user
          ? 'this invitation has already been answered. your circles are up to date.'
          : 'this invitation has already been answered. log in to see your circles.'}
        linkLabel={user ? 'go to circles →' : 'log in →'}
        linkTo={user ? '/circles' : '/login'}
      />
    );
  }

  // ── wrong account / own invitation ────────────────────────────────────────
  if (view?.viewerRole === 'inviter') {
    return (
      <TerminalState
        heading="this is your invitation"
        body="you sent this one. waiting for them to open it — you can check the circle any time."
        linkLabel="go to your circles →"
        linkTo="/circles"
      />
    );
  }

  if (view?.viewerRole === 'stranger') {
    return (
      <div style={SHELL}>
        <div style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
            this invitation isn&apos;t for you
          </h1>
          <p style={{ margin: 0, color: 'var(--text-muted, rgba(220,200,240,0.7))', lineHeight: 1.55 }}>
            you&apos;re signed in as someone other than the person who was invited. sign out and log
            in with the account the invitation was sent to — you&apos;ll come straight back here.
          </p>
          <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={async () => {
                try {
                  await logout();
                } catch (err) {
                  console.warn('sign-out failed', err);
                }
                // PPL-180 — hand off to /login with a redirect back here (+
                // action=accept) so the right account lands straight in the
                // auto-accept path. A bare reload of /c/:token would just show
                // the signed-out landing again.
                window.location.href = `/login?redirect=${encodeURIComponent(`/c/${token}?action=accept`)}`;
              }}
              style={{
                appearance: 'none', cursor: 'pointer', border: 0,
                padding: '12px 22px', borderRadius: 999,
                background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                boxShadow: '0 8px 24px rgba(217,70,239,0.32)',
              }}
            >
              sign out + log in
            </button>
            <Link to="/circles" style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
              go to your circles instead →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── main landing ──────────────────────────────────────────────────────────
  const circle = view?.circle ?? null;
  const inviterName = view?.inviter?.firstName || 'someone';
  const isAccepting = responding === 'accept';

  return (
    <div style={SHELL}>
      <div style={{ maxWidth: 460, width: '100%' }}>
        <div style={{
          borderRadius: 20, overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(30,14,40,0.96), rgba(18,8,26,0.96))',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}>
          <div style={{ height: 6, background: 'linear-gradient(90deg, #d946ef, #8b5cf6)' }} />
          <div style={{ padding: 26 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              {view?.inviter?.profileImageUrl ? (
                <img
                  src={view.inviter.profileImageUrl}
                  alt=""
                  style={{
                    width: 40, height: 40, borderRadius: '50%', objectFit: 'cover',
                    border: '1px solid rgba(255,255,255,0.12)', flexShrink: 0,
                  }}
                />
              ) : null}
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(232,220,250,0.75)', lineHeight: 1.5 }}>
                <b style={{ color: '#fff', fontWeight: 700 }}>{inviterName}</b> invited you to join a circle
              </p>
            </div>

            {circle?.imageUrl && (
              <img
                src={circle.imageUrl}
                alt=""
                style={{
                  width: '100%', maxHeight: 160, objectFit: 'cover',
                  borderRadius: 14, marginBottom: 16, display: 'block',
                }}
              />
            )}

            {/* No visibility badge: the public resolver deliberately doesn't
                return `visibility`, so we must not imply public or private. */}
            <h1 style={{
              margin: '0 0 10px', fontSize: 24, fontWeight: 800, color: '#fff',
              letterSpacing: '-0.025em',
            }}>
              {circle?.name || 'a circle on pplkpr'}
            </h1>

            {typeof circle?.memberCount === 'number' && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 14,
                fontSize: 12.5, color: 'rgba(232,220,250,0.6)',
              }}>
                <Users size={13} /> {circle.memberCount} {circle.memberCount === 1 ? 'member' : 'members'}
              </div>
            )}

            {circle?.description && (
              <p style={{
                margin: '0 0 20px', fontSize: 14, lineHeight: 1.6,
                color: 'rgba(232,220,250,0.8)',
              }}>
                {circle.description}
              </p>
            )}

            <div style={{
              padding: '11px 14px', borderRadius: 12, marginBottom: 20,
              background: 'rgba(217,70,239,0.07)', border: '1px solid rgba(217,70,239,0.2)',
              fontSize: 12.5, color: '#f3e8ff', lineHeight: 1.55,
            }}>
              accepting adds you straight away — {inviterName} already approved you. inside a
              circle you go by your first name, not your handle.
            </div>

            {responseError && (
              <div
                role="alert"
                style={{
                  padding: '10px 13px', borderRadius: 12, marginBottom: 14,
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                  fontSize: 12.5, color: '#fca5a5', lineHeight: 1.5,
                }}
              >
                {responseError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                onClick={() => void handleResponse('accept')}
                disabled={!!responding}
                style={{
                  appearance: 'none', cursor: responding ? 'progress' : 'pointer', border: 0,
                  padding: '13px 22px', borderRadius: 999, width: '100%',
                  background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                  color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                  boxShadow: '0 8px 24px rgba(217,70,239,0.3)',
                  opacity: responding ? 0.7 : 1,
                }}
              >
                {isAccepting ? 'joining…' : user ? 'join the circle' : 'join — sign up first'}
              </button>
              <button
                type="button"
                onClick={() => void handleResponse('decline')}
                disabled={!!responding}
                style={{
                  appearance: 'none', cursor: responding ? 'progress' : 'pointer',
                  padding: '12px 22px', borderRadius: 999, width: '100%',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(232,220,250,0.75)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
                }}
              >
                {responding === 'decline' ? 'declining…' : 'no thanks'}
              </button>
            </div>

            <p style={{
              margin: '16px 0 0', fontSize: 11.5, color: 'rgba(232,220,250,0.45)',
              textAlign: 'center', lineHeight: 1.5,
            }}>
              declining is quiet and needs no account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
